"use client";

import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import Link from "next/link";
import {
  adminDeleteComment,
  createComment,
  deleteComment,
  hideComment,
  markArticleCommentsSeen,
  moderateCommentEdit,
  pinComment,
  restoreComment,
  toggleArticleCommentFollow,
  unpinComment,
  updateComment,
  voteComment,
} from "@/lib/comment-actions";
import { publicUserName, userInitials } from "@/lib/user-display";
import { formatDate } from "@/lib/utils";
import { DownvoteShotIcon, UpvoteShotIcon } from "@/components/site/comment-vote-icons";
import { FavoriteTeamMark } from "@/components/site/favorite-team-mark";
import type { ActionResult } from "@/lib/action-result";
import { CommentComposerTools, CommentRichContent } from "@/components/comments/comment-composer-tools";

export type CommentView = {
  id: string;
  articleId: string;
  parentId: string | null;
  content: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  deletedAt: Date | string | null;
  hiddenAt: Date | string | null;
  pinnedAt: Date | string | null;
  moderationReason: string | null;
  isGuest: boolean;
  userId: string | null;
  guestIdentityId: string | null;
  user: {
    id: string;
    username: string;
    displayName: string | null;
    email: string;
    role: string;
    isDeleted?: boolean | null;
    favoriteTeam?: { name: string; logoUrl: string | null; externalTeam?: { crest: string | null } | null } | null;
  } | null;
  guestIdentity: {
    id: string;
    displayName: string;
  } | null;
  voteScore: number;
  upvotes: number;
  downvotes: number;
  currentUserVote: number;
};

function score(comment: CommentView) {
  return comment.voteScore;
}

function voteCounts(comment: CommentView) {
  return {
    up: comment.upvotes,
    down: comment.downvotes,
  };
}

function commentAuthorName(comment: CommentView) {
  if (comment.isGuest) return comment.guestIdentity?.displayName ?? "Vendég kommentelő";
  return comment.user ? publicUserName(comment.user) : "Törölt felhasználó";
}

function commentAuthorInitials(comment: CommentView) {
  if (!comment.isGuest && comment.user) return userInitials(comment.user);
  const name = comment.guestIdentity?.displayName ?? "Vendég";
  const initials = name
    .replace(/#\d+$/u, "")
    .split(/(?=[A-ZÁÉÍÓÖŐÚÜŰ])|\s+/u)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  return initials || "V";
}

function CommentForm({
  action,
  placeholder,
  submitLabel,
  defaultValue = "",
  defaultMention = "",
  parentId,
  onDone,
  onCreated,
}: {
  action: (formData: FormData) => Promise<ActionResult<CommentView> | void> | ActionResult<CommentView> | void;
  placeholder: string;
  submitLabel: string;
  defaultValue?: string;
  defaultMention?: string;
  parentId?: string;
  onDone?: () => void;
  onCreated?: (comment: CommentView) => void;
}) {
  const [content, setContent] = useState(defaultValue || defaultMention);
  const [message, setMessage] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function insertText(value: string) {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? content.length;
    const end = textarea?.selectionEnd ?? content.length;
    const spacer = start > 0 && !/\s$/.test(content.slice(0, start)) ? " " : "";
    const nextContent = `${content.slice(0, start)}${spacer}${value}${content.slice(end)}`;
    const nextCursor = start + spacer.length + value.length;

    setContent(nextContent);
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        const form = event.currentTarget;
        const formData = new FormData(form);
        setMessage(null);
        startTransition(async () => {
          try {
            const result = await action(formData);
            if (result && !result.ok) {
              setMessage(result.message);
              return;
            }
            setContent("");
            form.reset();
            setMessage(result?.message ?? null);
            if (result?.ok && result.data) onCreated?.(result.data);
            onDone?.();
          } catch (error) {
            console.error("[comment-ui] form submit failed", error);
            setMessage("A művelet most nem sikerült. Próbáld újra később.");
          }
        });
      }}
      className="grid gap-3"
    >
      {parentId ? <input type="hidden" name="parentId" value={parentId} /> : null}
      <textarea
        ref={textareaRef}
        name="content"
        required
        minLength={2}
        value={content}
        onChange={(event) => setContent(event.currentTarget.value)}
        placeholder={placeholder}
        className="min-h-24 w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900 outline-none ring-[#A4243B]/20 placeholder:text-zinc-400 focus:border-[#A4243B] focus:ring-4"
      />
      {message ? (
        <p className="rounded-lg bg-[#A4243B]/10 px-3 py-2 text-sm font-semibold text-[#A4243B]" role="alert">
          {message}
        </p>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2">
        <CommentComposerTools onInsert={insertText} />
        {onDone ? (
          <button
            type="button"
            onClick={onDone}
            className="rounded-lg border border-zinc-200 px-3 py-2 text-sm font-bold text-zinc-600 hover:border-zinc-400"
          >
            Mégse
          </button>
        ) : null}
        <button disabled={pending} className="rounded-lg bg-[#111] px-4 py-2 text-sm font-black uppercase text-white hover:bg-[#A4243B] disabled:cursor-wait disabled:opacity-60">
          {pending ? "Küldés..." : submitLabel}
        </button>
      </div>
    </form>
  );
}

function GuestCommentForm({
  action,
  onCreated,
  parentId,
  defaultMention = "",
  onDone,
}: {
  action: (formData: FormData) => Promise<ActionResult<CommentView> | void> | ActionResult<CommentView> | void;
  onCreated?: (comment: CommentView) => void;
  parentId?: string;
  defaultMention?: string;
  onDone?: () => void;
}) {
  const [email, setEmail] = useState("");
  const [content, setContent] = useState(defaultMention);
  const [message, setMessage] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function insertText(value: string) {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? content.length;
    const end = textarea?.selectionEnd ?? content.length;
    const spacer = start > 0 && !/\s$/.test(content.slice(0, start)) ? " " : "";
    const nextContent = `${content.slice(0, start)}${spacer}${value}${content.slice(end)}`;
    const nextCursor = start + spacer.length + value.length;

    setContent(nextContent);
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  }

  return (
    <div className="rounded-xl border border-[#D8C99B]/60 bg-[#FCFBF8] p-4">
      <div className="mb-3">
        <p className="text-xs font-black uppercase tracking-[0.14em] text-[#A4243B]">Vendég kommentelés</p>
        <h3 className="mt-1 text-lg font-black text-[#111]">Kommentelj gyorsan vendégként</h3>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const form = event.currentTarget;
          const formData = new FormData(form);
          setMessage(null);
          startTransition(async () => {
            try {
              const result = await action(formData);
              if (result && !result.ok) {
                setMessage(result.message);
                return;
              }
              setContent("");
              form.reset();
              setMessage(result?.message ?? "A hozzászólás elküldve.");
              if (result?.ok && result.data) onCreated?.(result.data);
              onDone?.();
            } catch (error) {
              console.error("[comment-ui] guest form submit failed", error);
              setMessage("A művelet most nem sikerült. Próbáld újra később.");
            }
          });
        }}
        className="grid gap-3"
      >
        {parentId ? <input type="hidden" name="parentId" value={parentId} /> : null}
        <div aria-hidden="true" className="pointer-events-none absolute -left-[9999px] h-px w-px overflow-hidden">
          <input
            name="companyWebsite"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            defaultValue=""
          />
        </div>
        <input
          name="email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.currentTarget.value)}
          placeholder="pelda@gmail.com"
          className="h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-sm font-semibold text-zinc-900 outline-none ring-[#A4243B]/15 placeholder:text-zinc-400 focus:border-[#A4243B] focus:ring-4"
        />
        <textarea
          ref={textareaRef}
          name="content"
          required
          minLength={2}
          value={content}
          onChange={(event) => setContent(event.currentTarget.value)}
          placeholder="Komment"
          className="min-h-24 w-full rounded-lg border border-zinc-200 bg-white px-3 py-3 text-sm text-zinc-900 outline-none ring-[#A4243B]/15 placeholder:text-zinc-400 focus:border-[#A4243B] focus:ring-4"
        />
        {message ? (
          <p className="rounded-lg bg-white px-3 py-2 text-sm font-semibold text-[#273E47]" role="status">
            {message}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center justify-end gap-3">
          <CommentComposerTools onInsert={insertText} />
          {onDone ? (
            <button
              type="button"
              onClick={onDone}
              className="rounded-lg border border-zinc-200 px-3 py-2 text-sm font-bold text-zinc-600 hover:border-zinc-400"
            >
              Mégse
            </button>
          ) : null}
          <button disabled={pending} className="rounded-lg bg-[#111] px-4 py-2 text-sm font-black uppercase text-white hover:bg-[#A4243B] disabled:cursor-wait disabled:opacity-60">
            {pending ? "Küldés..." : "Komment küldése"}
          </button>
        </div>
      </form>
    </div>
  );
}

function VoteButtons({
  comment,
  currentUserId,
}: {
  comment: CommentView;
  currentUserId: string | null;
}) {
  const [currentVote, setCurrentVote] = useState(comment.currentUserVote);
  const [counts, setCounts] = useState(() => voteCounts(comment));
  const [message, setMessage] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  function applyVote(value: 1 | -1) {
    if (!currentUserId || isPending) return;
    const previousVote = currentVote;
    const previousCounts = counts;
    const nextVote = previousVote === value ? 0 : value;
    setCurrentVote(nextVote);
    setCounts({
      up: previousCounts.up + (nextVote === 1 ? 1 : 0) - (previousVote === 1 ? 1 : 0),
      down: previousCounts.down + (nextVote === -1 ? 1 : 0) - (previousVote === -1 ? 1 : 0),
    });
    startTransition(async () => {
      try {
        const result = await voteComment(comment.id, value);
        if (!result.ok) {
          setCurrentVote(previousVote);
          setCounts(previousCounts);
          setMessage(result.message);
          return;
        }
        if (!result.data) throw new Error("missing_vote_result");
        setCurrentVote(result.data.currentUserVote);
        setCounts({ up: result.data.upvotes, down: result.data.downvotes });
      } catch (error) {
        console.error("[comment-ui] vote failed", error);
        setCurrentVote(previousVote);
        setCounts(previousCounts);
        setMessage("A szavazás most nem sikerült.");
      }
    });
  }

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-1.5 whitespace-nowrap sm:gap-2">
        <button
          type="button"
          onClick={() => applyVote(1)}
          disabled={!currentUserId || isPending}
          className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-2 text-sm font-black transition disabled:cursor-not-allowed disabled:opacity-45 sm:px-2.5 ${
            currentVote === 1
              ? "border-[#A4243B] bg-[#A4243B]/10 text-[#273E47]"
              : "border-zinc-200 bg-white text-zinc-700 hover:border-[#A4243B]"
          }`}
          title="Upvote"
          aria-label="Upvote"
        >
          <UpvoteShotIcon active={currentVote === 1} />
          <span>{counts.up}</span>
        </button>
        <button
          type="button"
          onClick={() => applyVote(-1)}
          disabled={!currentUserId || isPending}
          className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-2 text-sm font-black transition disabled:cursor-not-allowed disabled:opacity-45 sm:px-2.5 ${
            currentVote === -1
              ? "border-[#A4243B] bg-[#A4243B]/10 text-[#A4243B]"
              : "border-zinc-200 bg-white text-zinc-700 hover:border-[#A4243B]"
          }`}
          title="Downvote"
          aria-label="Downvote"
        >
          <DownvoteShotIcon active={currentVote === -1} />
          <span>{counts.down}</span>
        </button>
        {message ? <span className="text-xs font-semibold text-[#A4243B]">{message}</span> : null}
    </div>
  );
}

function ModerationMenu({
  comment,
  onModeratedEdit,
}: {
  comment: CommentView;
  onModeratedEdit: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    const updatePlacement = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;

      const estimatedMenuHeight = 208;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUpward(spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow);
    };

    updatePlacement();
    window.addEventListener("resize", updatePlacement);
    window.addEventListener("scroll", updatePlacement, true);
    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("resize", updatePlacement);
      window.removeEventListener("scroll", updatePlacement, true);
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const menuPositionClass = openUpward ? "bottom-full mb-2" : "top-full mt-2";

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="cursor-pointer rounded-md border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-black uppercase text-zinc-600 transition hover:border-[#273E47] hover:text-[#273E47]"
      >
        Moderálás
      </button>
      {open ? (
        <div
          className={`absolute right-0 z-50 grid w-[200px] gap-1 rounded-md border border-black/[0.1] bg-white p-1.5 shadow-[0_10px_30px_rgba(15,23,42,0.12)] ${menuPositionClass}`}
        >
          {comment.hiddenAt ? (
            <form
              action={() => restoreComment(comment.id)}
              onSubmit={() => setOpen(false)}
            >
              <button className="w-full rounded-sm px-3 py-2 text-left text-sm font-semibold text-[#273E47] hover:bg-zinc-100">
                Visszaállítás
              </button>
            </form>
          ) : (
            <form
              action={hideComment.bind(null, comment.id)}
              onSubmit={() => setOpen(false)}
            >
              <button className="w-full rounded-sm px-3 py-2 text-left text-sm font-semibold text-[#273E47] hover:bg-zinc-100">
                Elrejtés
              </button>
            </form>
          )}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onModeratedEdit();
            }}
            className="w-full rounded-sm px-3 py-2 text-left text-sm font-semibold text-[#273E47] hover:bg-zinc-100"
          >
            Moderált szerkesztés
          </button>
          {comment.pinnedAt ? (
            <form
              action={() => unpinComment(comment.id)}
              onSubmit={() => setOpen(false)}
            >
              <button className="w-full rounded-sm px-3 py-2 text-left text-sm font-semibold text-[#273E47] hover:bg-zinc-100">
                Kiemelés levétele
              </button>
            </form>
          ) : (
            <form
              action={() => pinComment(comment.id)}
              onSubmit={() => setOpen(false)}
            >
              <button className="w-full rounded-sm px-3 py-2 text-left text-sm font-semibold text-[#273E47] hover:bg-zinc-100">
                Kiemelés
              </button>
            </form>
          )}
          <form
            action={adminDeleteComment.bind(null, comment.id)}
            onSubmit={() => setOpen(false)}
          >
            <button className="w-full rounded-sm px-3 py-2 text-left text-sm font-semibold text-[#A4243B] hover:bg-[#A4243B]/6">
              Törlés
            </button>
          </form>
        </div>
      ) : null}
    </div>
  );
}

function CommentItem({
  comment,
  replies,
  totalReplyCount,
  currentUserId,
  canModerate,
  onReplyCreated,
}: {
  comment: CommentView;
  replies: CommentView[];
  totalReplyCount: number;
  currentUserId: string | null;
  canModerate: boolean;
  onReplyCreated?: (parentId: string) => void;
}) {
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [loadedReplies, setLoadedReplies] = useState(replies);
  const [localTotalReplyCount, setLocalTotalReplyCount] = useState(totalReplyCount);
  const [hasLoadedAllReplies, setHasLoadedAllReplies] = useState(replies.length >= totalReplyCount);
  const [isLoadingReplies, startRepliesTransition] = useTransition();
  const isOwner = Boolean(currentUserId && comment.userId && currentUserId === comment.userId);
  const isDeleted = Boolean(comment.deletedAt);
  const canEditAsModerator = canModerate && !isOwner;
  const createReply = createComment.bind(null, comment.articleId);
  const update = canEditAsModerator ? moderateCommentEdit.bind(null, comment.id) : updateComment.bind(null, comment.id);
  const authorName = commentAuthorName(comment);
  const replyMention = `@${authorName} `;

  function loadMoreReplies() {
    if (isLoadingReplies || hasLoadedAllReplies) return;
    startRepliesTransition(async () => {
      try {
        const response = await fetch(
          `/api/articles/${comment.articleId}/comments/${comment.id}/replies?skip=${loadedReplies.length}&take=20`,
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = (await response.json()) as {
          comments: CommentView[];
          hasMoreReplies: boolean;
        };
        setLoadedReplies((current) => {
          const existingIds = new Set(current.map((item) => item.id));
          return [...current, ...payload.comments.filter((item) => !existingIds.has(item.id))];
        });
        setHasLoadedAllReplies(!payload.hasMoreReplies);
      } catch (error) {
        console.error("[comment-ui] reply pagination failed", error);
      }
    });
  }

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-3 sm:p-4 md:p-5">
      <div className="flex min-w-0 gap-2 sm:gap-3 md:gap-4">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#1A1A1A] text-xs font-black text-white sm:h-10 sm:w-10 sm:text-sm">
          {commentAuthorInitials(comment)}
        </div>
        <div className="min-w-0 flex-1 overflow-visible">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-black text-zinc-950">{authorName}</p>
                {comment.user?.favoriteTeam ? <FavoriteTeamMark team={comment.user.favoriteTeam} /> : null}
                {comment.isGuest ? (
                  <span className="rounded-full border border-[#D8C99B]/70 bg-[#F6F1E3] px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] text-[#6B6256]">
                    Vendég
                  </span>
                ) : null}
                {comment.pinnedAt ? (
                  <span className="rounded-full bg-[#A4243B]/15 px-2 py-0.5 text-xs font-black uppercase text-[#273E47]">
                    Kiemelt
                  </span>
                ) : null}
              </div>
              <p className="text-xs font-semibold uppercase text-zinc-400">
                {formatDate(comment.createdAt)}
              </p>
            </div>
            {canModerate ? <ModerationMenu comment={comment} onModeratedEdit={() => setEditing(true)} /> : null}
          </div>

          {editing && !isDeleted ? (
            <div className="mt-3">
              <CommentForm
                action={update}
                placeholder="Komment szerkesztése"
                submitLabel="Mentés"
                defaultValue={comment.content}
                onDone={() => setEditing(false)}
              />
            </div>
          ) : (
            <CommentRichContent
              content={isDeleted ? "Komment törölve." : comment.content}
              className={`mt-4 whitespace-pre-wrap text-sm leading-6 ${
                isDeleted ? "italic text-zinc-400" : "text-zinc-800"
              }`}
            />
          )}

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-zinc-100 pt-3">
            {!isDeleted ? (
              <VoteButtons
                key={`${comment.id}:${comment.currentUserVote}:${comment.upvotes}:${comment.downvotes}`}
                comment={comment}
                currentUserId={currentUserId}
              />
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              {!isDeleted && !comment.parentId ? (
                <button
                  type="button"
                  onClick={() => setReplying(true)}
                  className="rounded-md border border-zinc-200 px-2.5 py-1.5 text-xs font-black uppercase text-zinc-500 hover:border-[#A4243B] hover:text-[#A4243B]"
                >
                  Válasz
                </button>
              ) : null}
              {isOwner && !isDeleted ? (
                <>
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="rounded-md border border-zinc-200 px-2.5 py-1.5 text-xs font-black uppercase text-zinc-500 hover:border-[#A4243B] hover:text-[#A4243B]"
                  >
                    Szerkesztés
                  </button>
                  <form action={() => deleteComment(comment.id)}>
                    <button className="rounded-md border border-zinc-200 px-2.5 py-1.5 text-xs font-black uppercase text-zinc-500 hover:border-[#A4243B] hover:text-[#A4243B]">
                      Törlés
                    </button>
                  </form>
                </>
              ) : null}
              {canEditAsModerator && !isDeleted ? (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="rounded-md border border-zinc-200 px-2.5 py-1.5 text-xs font-black uppercase text-zinc-500 hover:border-[#A4243B] hover:text-[#A4243B]"
                >
                  Moderált szerkesztés
                </button>
              ) : null}
            </div>
          </div>

          {replying ? (
            <div className="mt-5 border-l-4 border-[#A4243B]/30 pl-3 sm:pl-4">
              {currentUserId ? (
                <CommentForm
                  action={createReply}
                  parentId={comment.id}
                  placeholder={`Válasz ${authorName} kommentjére`}
                  submitLabel="Válasz küldése"
                  defaultMention={replyMention}
                  onCreated={(reply) => {
                    setLoadedReplies((current) => {
                      if (current.some((item) => item.id === reply.id)) return current;
                      return [...current, reply];
                    });
                    setLocalTotalReplyCount((current) => current + 1);
                    setHasLoadedAllReplies(true);
                    onReplyCreated?.(comment.id);
                  }}
                  onDone={() => setReplying(false)}
                />
              ) : (
                <GuestCommentForm
                  action={createReply}
                  parentId={comment.id}
                  defaultMention={replyMention}
                  onCreated={(reply) => {
                    setLoadedReplies((current) => {
                      if (current.some((item) => item.id === reply.id)) return current;
                      return [...current, reply];
                    });
                    setLocalTotalReplyCount((current) => current + 1);
                    setHasLoadedAllReplies(true);
                    onReplyCreated?.(comment.id);
                  }}
                  onDone={() => setReplying(false)}
                />
              )}
            </div>
          ) : null}

          {loadedReplies.length > 0 ? (
            <div className="mt-5 grid gap-3 border-l-4 border-zinc-100 pl-3 sm:gap-4 sm:pl-4">
              {loadedReplies.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  replies={[]}
                  totalReplyCount={0}
                  currentUserId={currentUserId}
                  canModerate={canModerate}
                />
              ))}
              {!hasLoadedAllReplies ? (
                <button
                  type="button"
                  onClick={loadMoreReplies}
                  disabled={isLoadingReplies}
                  className="w-fit rounded-md border border-zinc-200 px-3 py-2 text-xs font-black uppercase text-zinc-500 transition hover:border-[#A4243B] hover:text-[#A4243B] disabled:cursor-wait disabled:opacity-60"
                >
                  {isLoadingReplies ? "Betöltés..." : `További válaszok (${Math.max(0, localTotalReplyCount - loadedReplies.length)})`}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function CommentSection({
  articleId,
  comments,
  currentUserId,
  canModerate,
  isFollowing,
  unseenFollowedCommentCount,
  hasMoreComments,
  commentPages,
  totalComments,
  replyCountsByParent,
}: {
  articleId: string;
  comments: CommentView[];
  currentUserId: string | null;
  canModerate: boolean;
  isFollowing: boolean;
  unseenFollowedCommentCount: number;
  hasMoreComments: boolean;
  commentPages: number;
  totalComments: number;
  replyCountsByParent: Record<string, number>;
}) {
  const [open, setOpen] = useState(false);
  const [localComments, setLocalComments] = useState(comments);
  const [localCommentCount, setLocalCommentCount] = useState(totalComments);
  const [localReplyCountsByParent, setLocalReplyCountsByParent] = useState(replyCountsByParent);
  const [, startTransition] = useTransition();
  const createRootComment = createComment.bind(null, articleId);
  const visibleCount = localCommentCount;
  const topComment = useMemo(
    () =>
      [...localComments]
        .filter((comment) => !comment.parentId && !comment.deletedAt)
        .sort((a, b) => score(b) - score(a))[0],
    [localComments],
  );
  const roots = localComments
    .filter((comment) => !comment.parentId)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const repliesByParent = new Map<string, CommentView[]>();
  localComments
    .filter((comment) => comment.parentId)
    .forEach((comment) => {
      const existing = repliesByParent.get(comment.parentId ?? "") ?? [];
      repliesByParent.set(comment.parentId ?? "", [...existing, comment]);
    });
  for (const [parentId, replies] of repliesByParent) {
    repliesByParent.set(parentId, [...replies].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()));
  }

  function addRootComment(comment: CommentView) {
    setLocalComments((current) => {
      if (current.some((item) => item.id === comment.id)) return current;
      return [...current, comment];
    });
    setLocalCommentCount((current) => current + 1);
  }

  function addReply(parentId: string) {
    setLocalCommentCount((current) => current + 1);
    setLocalReplyCountsByParent((current) => ({
      ...current,
      [parentId]: (current[parentId] ?? 0) + 1,
    }));
  }

  return (
    <section id="comments" className="mt-10 max-w-full scroll-mt-32 rounded-xl border border-zinc-200 bg-white p-3 text-zinc-950 sm:p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black uppercase">Kommentek</h2>
          <p className="text-sm font-semibold text-zinc-500">{visibleCount} hozzászólás</p>
          {!open && topComment ? (
            <p className="mt-2 max-w-2xl truncate text-sm text-zinc-600">
              <span className="font-black">{commentAuthorName(topComment)}:</span> {topComment.content}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => {
            setOpen((value) => {
              const next = !value;
              if (next && currentUserId && isFollowing) {
                startTransition(() => {
                  void markArticleCommentsSeen(articleId);
                });
              }
              return next;
            });
          }}
          className="rounded-lg bg-[#111] px-4 py-2 text-sm font-black uppercase text-white hover:bg-[#A4243B]"
        >
          {open ? "Bezárás" : "Megnyitás"}
        </button>
      </div>

      {open ? (
        <div className="mt-5 grid gap-5">
          {currentUserId ? (
            <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
              <form action={async () => {
                await toggleArticleCommentFollow(articleId);
              }}>
                <label className="flex cursor-pointer items-start justify-between gap-4 text-sm font-bold text-zinc-700">
                  <span>
                    <span className="block text-zinc-950">
                      Értesítést kérek az új kommentekről
                    </span>
                    <span className="mt-1 block text-xs font-semibold text-zinc-500">
                      {isFollowing
                        ? unseenFollowedCommentCount > 0
                          ? `${unseenFollowedCommentCount} új komment a legutóbbi megtekintés óta`
                          : "Követed ezt a beszélgetést"
                        : "Kapcsold be, ha követni szeretnéd ezt a beszélgetést."}
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={isFollowing}
                    onChange={(event) => event.currentTarget.form?.requestSubmit()}
                    className="mt-1 h-5 w-5 accent-[#A4243B]"
                  />
                </label>
              </form>
            </div>
          ) : null}
          {currentUserId ? (
            <CommentForm
              action={createRootComment}
              placeholder="Szólj hozzá. @név vagy @emailprefix említéssel értesítést küldesz."
              submitLabel="Komment küldése"
              onCreated={addRootComment}
            />
          ) : (
            <GuestCommentForm action={createRootComment} onCreated={addRootComment} />
          )}
          <div className="grid gap-6">
            {roots.length > 0 ? (
              roots.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  replies={repliesByParent.get(comment.id) ?? []}
                  totalReplyCount={localReplyCountsByParent[comment.id] ?? repliesByParent.get(comment.id)?.length ?? 0}
                  currentUserId={currentUserId}
                  canModerate={canModerate}
                  onReplyCreated={addReply}
                />
              ))
            ) : (
              <p className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 text-sm font-semibold text-zinc-500">
                Még nincs komment. Legyél te az első.
              </p>
            )}
            {hasMoreComments ? (
              <Link
                href={`?commentsPage=${commentPages + 1}#comments`}
                className="w-fit rounded-lg border border-zinc-200 px-4 py-2 text-sm font-black uppercase text-zinc-600 transition hover:border-[#A4243B] hover:text-[#A4243B]"
              >
                További kommentek betöltése
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
